import {
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack
} from "@fluentui/react";
import { FormikHelpers } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { Contract } from "@/components/Contracts/contract.interface";
import { ownershipTransferFieldsSchema } from "@/components/Contracts/OwnershipTransferStep/fields/ownership-transfer-fields-schema";
import { SubmitOwnershipTransferFieldsValues } from "@/components/Contracts/OwnershipTransferStep/fields/ownership-transfer-fields.interface";
import { ownershipTransferFields } from "@/components/Contracts/OwnershipTransferStep/fields/OwnershipTransferFields";
import { CustomDrawer } from "@/components/Shared/CustomDrawer";
import { useApi } from "@/core/libs/api/react-query/useApi";
import FormBuilder from "@/core/libs/form-builder";
import { IMessageBar, messageBarTypes } from "@/core/libs/message-bar";
import { AccountTypeChoiceGroup } from "@/modules/Accounts/components/AccountTypeChoiceGroup";
import { AccountType } from "@/modules/Accounts/types";
import { TermTypeEnum } from "@/modules/Terms/interfaces/ITerm.interface";

interface OwnershipTransferTermDraftDrawerProps {
  contract: Contract;
  isOpen: boolean;
  onClose: () => void;
  refetchContractData: () => Promise<void>;
  setMessage: (message: IMessageBar) => void;
}

const sanitizeNumber = (value?: string) => value?.replace(/\D/g, "");
const sanitizeDocument = (value?: string) =>
  value?.replace(/[^\p{L}\p{N}]/gu, "");

function buildTermDataDraft(
  contract: Contract,
  values: SubmitOwnershipTransferFieldsValues,
  accountType: AccountType
) {
  const address = values.addresses?.[0];

  return {
    termType: TermTypeEnum.CHANGE_OF_OWNERSHIP,
    fullName: values.fullName,
    accountType,
    documentNumber: sanitizeNumber(values.documentNumber),
    zipCode: String(address?.zipCode || "").replace(/\D/g, ""),
    city: address?.city,
    uf: address?.state,
    street: address?.street,
    neighborhood: address?.neighborhood,
    number: address?.number,
    contractId: contract.id,
    whatsapp: sanitizeNumber(values.whatsapp),
    email: values.email,
    secondaryDocument: sanitizeDocument(values.secondaryDocument),
    note: address?.note,
    legalRepresentativeName: values.legalRepresentativeName,
    legalRepresentativeDocument: sanitizeNumber(
      values.legalRepresentativeDocument
    )
  };
}

export function OwnershipTransferTermDraftDrawer({
  contract,
  isOpen,
  onClose,
  refetchContractData,
  setMessage
}: OwnershipTransferTermDraftDrawerProps): JSX.Element {
  const { execute } = useApi();
  const [accountType, setAccountType] = useState<AccountType>();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (isOpen) {
      setAccountType(undefined);
      setErrorMessage(undefined);
    }
  }, [isOpen]);

  const fields = useMemo(
    () =>
      ownershipTransferFields({
        accountType: accountType ?? AccountType.PHYSICAL,
        documentNumber: ""
      }),
    [accountType]
  );

  const validationSchema = useMemo(
    () => ownershipTransferFieldsSchema(accountType),
    [accountType]
  );

  const handleSubmit = useCallback(
    async (
      values: SubmitOwnershipTransferFieldsValues,
      { setSubmitting }: FormikHelpers<SubmitOwnershipTransferFieldsValues>
    ) => {
      setSubmitting(true);
      setErrorMessage(undefined);

      const lineNumbers = contract.items?.map(item => item.line?.number);
      const carrierId = contract.items[0]?.line.carrierId;

      try {
        const bodyObj = {
          termDataDraft: buildTermDataDraft(contract, values, accountType),
          signatureId: contract.signatureId,
          carrierId,
          lineNumbers
        };

        const response = await execute({
          url: `contracts/${contract.id}/terms/change-of-ownership`,
          method: "POST",
          bodyObj
        });

        if (response?.response?.ok) {
          setMessage({
            message: "Documento complementar criado com sucesso.",
            type: messageBarTypes.SUCCESS
          });

          await refetchContractData();
          onClose();
        } else {
          setErrorMessage(
            response?.error?.message || "Erro ao criar documento complementar."
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
    [accountType, contract, execute, onClose, refetchContractData, setMessage]
  );

  return (
    <CustomDrawer
      isOpen={isOpen}
      onCloseDrawer={onClose}
      action="Criar"
      module="Documento Complementar"
    >
      <Stack>
        <AccountTypeChoiceGroup
          accountType={accountType}
          onChange={setAccountType}
        />

        {accountType && (
          <>
            <p>Informe os dados do titular na operadora de origem.</p>

            <FormBuilder
              fields={fields}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {errorMessage && (
                <MessageBar messageBarType={MessageBarType.error} isMultiline>
                  {errorMessage}
                </MessageBar>
              )}

              <PrimaryButton type="submit" style={{ width: "200px" }}>
                Criar documento
              </PrimaryButton>
            </FormBuilder>
          </>
        )}
      </Stack>
    </CustomDrawer>
  );
}
